import React, { useEffect, useState } from "react";
import { CalendarDays, Trophy, Gift, Flame, ExternalLink } from "lucide-react";
import Countdown from "@/components/Countdown";
import SectionLabel from "@/components/SectionLabel";
import { getTopDepositors } from "@/lib/api";
import { config } from "@/config";

const usd = (n) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n || 0);
const usd2 = (n) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

function PoolCard({ testid, icon: Icon, title, amount, qualified, sharing, reset, resetTestid, eligible, full }) {
  return (
    <div data-testid={testid} className={`card-glow p-4 ${full ? "col-span-2" : ""}`}>
      <div className="mb-2 flex items-center justify-between">
        <span className="grad-label">{title}</span>
        <Icon size={18} className="text-[#34D07A]" />
      </div>
      <div className="truncate text-xl font-extrabold text-white" style={{ fontFamily: "Unbounded, Inter, sans-serif" }}>
        ${usd2(amount)}
      </div>
      <div className="mt-2 flex flex-col gap-1 text-[11px]">
        <div className="flex items-center justify-between">
          <span className="text-white/50">Qualified IDs</span>
          <b className="text-white/80">{qualified ?? 0}</b>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-white/50">Sharing</span>
          <b className="text-[#34D07A]">${usd2(sharing)}</b>
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1 rounded-full border border-[#0AA84F]/30 bg-[#0AA84F]/8 px-2 py-1 text-[10px] text-white/40">
          <span className="shrink-0">resets</span>
          <Countdown target={reset} testid={resetTestid} />
          <span className="shrink-0 text-white/30">UTC</span>
        </span>
        <span
          data-testid={`${testid}-eligibility`}
          className={`flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-1 text-[10px] font-semibold ${
            eligible
              ? "bg-[#0AA84F]/15 text-[#22C55E] border border-[#0AA84F]/50"
              : "bg-red-500/15 text-red-400 border border-red-500/45"
          }`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${eligible ? "bg-[#22C55E]" : "bg-red-400"}`} />
          {eligible ? "Eligible" : "Not Eligible"}
        </span>
      </div>
    </div>
  );
}

const shortA = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : "—");

function TopPoolCard({ data }) {
  const top3 = data?.top3 || [];
  const pct = [50, 30, 20];
  return (
    <div data-testid="gb-top-pool" className="card-glow col-span-2 p-4">
      <div className="mb-2 flex items-center justify-between">
        <span className="grad-label">TOP DEPOSITOR POOL</span>
        <Trophy size={18} className="text-[#34D07A]" />
      </div>
      <div className="truncate text-xl font-extrabold text-white" style={{ fontFamily: "Unbounded, Inter, sans-serif" }}>
        ${usd2(data?.pool_balance)}
      </div>
      <div className="mt-2 flex flex-col gap-1 text-[11px]">
        {[0, 1, 2].map((i) => {
          const t = top3[i];
          return (
            <div key={i} className="flex items-center justify-between">
              {t?.address ? (
                <a
                  href={t.tx_hash ? `${config.explorer}/tx/${t.tx_hash}` : `${config.explorer}/address/${t.address}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`gb-top-pool-link-${i + 1}`}
                  className="flex min-w-0 items-center gap-1.5 text-white/50 active:opacity-80"
                >
                  <span className="text-base leading-none">{["🥇", "🥈", "🥉"][i]}</span>
                  <span className="truncate font-mono text-[#34D07A] underline decoration-dotted underline-offset-2">{shortA(t.address)}</span>
                  <ExternalLink size={11} className="shrink-0 text-[#34D07A]" />
                </a>
              ) : (
                <span className="flex min-w-0 items-center gap-1.5 text-white/50">
                  <span className="text-base leading-none">{["🥇", "🥈", "🥉"][i]}</span>
                  <span className="truncate font-mono text-white/70">—</span>
                </span>
              )}
              <div className="flex shrink-0 items-center gap-2">
                <span className="w-9 text-right text-xs font-semibold text-white/50">{pct[i]}%</span>
                <b className="w-16 text-right text-[#34D07A]">${usd2(t?.reward || 0)}</b>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1 rounded-full border border-[#0AA84F]/30 bg-[#0AA84F]/8 px-2 py-1 text-[10px] text-white/40">
          <span className="shrink-0">resets</span>
          <Countdown target={data?.settle_at} testid="gb-top-pool-countdown" />
          <span className="shrink-0 text-white/30">UTC</span>
        </span>
        <span className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-[#FFA000]/45 bg-[#FFA000]/12 px-2 py-1 text-[10px] font-semibold text-[#FFC24B]">
          <Flame size={11} /> 24h Reward
        </span>
      </div>
    </div>
  );
}

export default function GlobalBusiness({ stats, me, pools }) {
  const [topPool, setTopPool] = useState(null);
  useEffect(() => {
    const run = () => getTopDepositors().then(setTopPool).catch(() => {});
    run();
    const t = setInterval(run, 30000);
    return () => clearInterval(t);
  }, []);
  const dailyElig = Boolean(pools?.daily?.qualified);
  const weeklyElig = Boolean(pools?.weekly?.qualified);
  const monthlyElig = Boolean(pools?.monthly?.qualified);
  return (
    <div>
      <SectionLabel center>Global Business</SectionLabel>
      <div className="flex flex-col gap-3">
        <PoolCard
          testid="gb-daily-pool" icon={CalendarDays} title="Daily Pool" full
          amount={pools?.daily?.balance} qualified={pools?.daily?.achievers} sharing={pools?.daily?.estimate}
          reset={stats?.resets?.daily} resetTestid="gb-daily-countdown" eligible={dailyElig}
        />
        <PoolCard
          testid="gb-weekly-pool" icon={Trophy} title="Weekly Pool" full
          amount={pools?.weekly?.balance} qualified={pools?.weekly?.achievers} sharing={pools?.weekly?.estimate}
          reset={stats?.resets?.weekly} resetTestid="gb-weekly-countdown" eligible={weeklyElig}
        />
        <PoolCard
          testid="gb-monthly-pool" icon={Gift} title="Monthly Pool" full
          amount={pools?.monthly?.balance} qualified={pools?.monthly?.achievers} sharing={pools?.monthly?.estimate}
          reset={stats?.resets?.monthly} resetTestid="gb-monthly-countdown" eligible={monthlyElig}
        />
        <TopPoolCard data={topPool} />
      </div>
    </div>
  );
}
